import React, { useState, useEffect, useRef } from "react";
import "./LocationDetailsTable.css";

const LocationDetailsTable = ({ commuteData, schoolRatingsData, onSelectionChange }) => {
  const [combinedData, setCombinedData] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  // Add ref to track whether auto-selection has already happened
  const hasAutoSelectedRef = useRef(false);

  // useEffect(() => {
  //   // onSelectionChange(selectedLocations);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [selectedLocations]);

  // Helper method to evaluate commute result value
  const getLocationCommuteResultValue = (location, commuteResults) => {
    let inMaxCommute = false;
    let inMaxCommuteDiviation = false;
    let outsideOfMaxCommute = false;

    // Check each constraint
    commuteResults.forEach((result) => {
      // Get arrays of locations for each category
      const maxCommuteLocations = result.commuteResult.inMaxCommute || [];
      const diviationLocations = result.commuteResult.inMaxCommuteDiviation || [];
      const outsideLocations = result.commuteResult.outsideOfMaxCommute || [];

      // Check if this location is in any of the arrays
      if (maxCommuteLocations.some((item) => item.location === location.placeId)) {
        inMaxCommute = true;
      }

      if (diviationLocations.some((item) => item.location === location.placeId)) {
        inMaxCommuteDiviation = true;
      }

      if (outsideLocations.some((item) => item.location === location.placeId)) {
        outsideOfMaxCommute = true;
      }
    });

    // Determine overall status - prioritize the worst case
    if (outsideOfMaxCommute) {
      return "outsideOfMaxCommute";
    } else if (inMaxCommuteDiviation) {
      return "inMaxCommuteDiviation";
    } else if (inMaxCommute) {
      return "inMaxCommute";
    } else {
      return "unknown";
    }
  };

  useEffect(() => {
    if (!commuteData || !schoolRatingsData) return;

    const locationInfoMap = commuteData.locationInfoMapping || [];
    const commuteResults = commuteData.commuteResults || [];
    const schoolRatings = schoolRatingsData.schoolRatingsPerLocation || [];

    // Create a lookup map for school ratings by address
    const schoolRatingsByAddress = {};
    schoolRatings.forEach((item) => {
      schoolRatingsByAddress[item.location.trim()] = item.schools;
    });

    // Process location data to combine with commute info and school ratings
    const processedData = locationInfoMap
      .filter((location) => !location.isConstraint) // Filter out constraint locations
      .map((location) => {
        // Store commute data for each constraint separately in an array
        // The index in this array will correspond to the index of the constraint in commuteResults
        const commuteTimesArray = new Array(commuteResults.length).fill(null);
        const commuteStatusArray = new Array(commuteResults.length).fill("unknown");

        // Process each constraint destination
        commuteResults.forEach((result, resultIndex) => {
          // Arrays containing location data for this constraint
          const inMaxCommute = result.commuteResult.inMaxCommute || [];
          const inMaxCommuteDiviation = result.commuteResult.inMaxCommuteDiviation || [];
          const outsideOfMaxCommute = result.commuteResult.outsideOfMaxCommute || [];

          // Check if this location is in any of the three categories for this constraint
          let foundItem = null;
          let status = "unknown";

          // Check in order: max commute, diviation, outside
          for (const item of inMaxCommute) {
            if (item.location === location.placeId) {
              foundItem = item;
              status = "inMaxCommute";
              break;
            }
          }

          if (!foundItem) {
            for (const item of inMaxCommuteDiviation) {
              if (item.location === location.placeId) {
                foundItem = item;
                status = "inMaxCommuteDiviation";
                break;
              }
            }
          }

          if (!foundItem) {
            for (const item of outsideOfMaxCommute) {
              if (item.location === location.placeId) {
                foundItem = item;
                status = "outsideOfMaxCommute";
                break;
              }
            }
          }

          // If this location has data for this constraint, store it at the correct index
          if (foundItem) {
            const duration = foundItem.duration;

            // Store at the exact index matching the constraint
            commuteTimesArray[resultIndex] = {
              noTraffic: duration.noTraffic,
              withTraffic: duration.withTraffic,
            };

            // Store the status as well
            commuteStatusArray[resultIndex] = status;
          }
        });

        // Find school ratings for this location
        const addressKey = location.locationAddress.trim();
        const schools = schoolRatingsByAddress[addressKey] || [];

        // Get commute status for this location
        const commuteStatus = getLocationCommuteResultValue(location, commuteResults);

        return {
          address: location.locationAddress,
          commuteTimesArray,
          commuteStatusArray,
          schools,
          placeId: location.placeId,
          commuteStatus,
        };
      });

    setCombinedData(processedData);

    // Initialize the selection state with all locations unselected
    const initialSelection = {};
    processedData.forEach((item, index) => {
      initialSelection[index] = false;
    });
    setSelectedLocations(initialSelection);

    // Reset the auto-selection flag when new data is loaded
    hasAutoSelectedRef.current = false;
  }, [commuteData, schoolRatingsData]);

  // Effect to notify parent component when selection changes
  useEffect(() => {
    // Only notify parent if we have data and a callback function
    if (combinedData.length > 0 && onSelectionChange) {
      const selectedItems = getSelectedLocations();
      onSelectionChange(selectedItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocations]);

  // Modified effect to automatically select green rows (inMaxCommute status)
  // This will only run once after data is loaded
  useEffect(() => {
    // Only run auto-selection once and only if we have data
    if (combinedData.length > 0 && !hasAutoSelectedRef.current) {
      // Create a new selection state based on commute status
      const autoSelection = {};
      let hasGreenRows = false;

      combinedData.forEach((item, index) => {
        // Select rows that have "inMaxCommute" status (green)
        const isGreen = item.commuteStatus === "inMaxCommute";
        autoSelection[index] = isGreen;

        if (isGreen) {
          hasGreenRows = true;
        }
      });

      // Only update if we found green rows
      if (hasGreenRows) {
        setSelectedLocations(autoSelection);

        // If all rows are selected, update the selectAll state
        const allSelected = Object.values(autoSelection).every(Boolean);
        setSelectAll(allSelected);

        // Log the auto-selection
        console.log(
          "Auto-selected green rows:",
          combinedData.filter((_, index) => autoSelection[index])
        );

        // Mark that we've done the auto-selection
        hasAutoSelectedRef.current = true;
      }
    }
  }, [combinedData]);

  // Handle individual checkbox change
  const handleCheckboxChange = (index) => {
    setSelectedLocations((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Handle select all checkbox change
  const handleSelectAllChange = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    const newSelections = {};
    combinedData.forEach((_, index) => {
      newSelections[index] = newSelectAll;
    });
    setSelectedLocations(newSelections);
  };

  // Get array of selected locations
  const getSelectedLocations = () => {
    return combinedData.filter((_, index) => selectedLocations[index]);
  };

  // Helper to get the appropriate CSS class based on commute status
  const getStatusClass = (status) => {
    switch (status) {
      case "inMaxCommute":
        return "in-max-commute";
      case "inMaxCommuteDiviation":
        return "in-max-commute-diviation";
      case "outsideOfMaxCommute":
        return "outside-of-max-commute";
      default:
        return "";
    }
  };

  // Helper to get rating class based on rating value
  const getRatingClass = (rating) => {
    if (rating >= 4.5) return "rating-4-5-plus";
    if (rating >= 4.0) return "rating-4-0-plus";
    if (rating >= 3.5) return "rating-3-5-plus";
    if (rating >= 3.0) return "rating-3-0-plus";
    return "rating-below-3";
  };

  if (!commuteData || !schoolRatingsData || combinedData.length === 0) {
    return <div>No data available. Please calculate commute times and school ratings first.</div>;
  }

  // Get constraints and ensure we have them in order
  const constraints = commuteData.commuteResults || [];

  // Count selected locations
  const selectedCount = Object.values(selectedLocations).filter(Boolean).length;

  return (
    <div>
      <h2 className="component-title">Location Details</h2>
      <h3 className="section-header">Commute Times and School Ratings</h3>

      {/* Add legend to explain row colors */}
      <div className="legend">
        <div className="legend-item">
          <div className="color-swatch color-swatch-green"></div>
          <span>Within max commute time</span>
        </div>
        <div className="legend-item">
          <div className="color-swatch color-swatch-orange"></div>
          <span>Within max commute deviation</span>
        </div>
        <div className="legend-item">
          <div className="color-swatch color-swatch-red"></div>
          <span>Outside max commute time</span>
        </div>
      </div>

      {/* Add selection controls */}
      <div className="selection-controls">
        <div>
          <input type="checkbox" id="select-all" className="checkbox" checked={selectAll} onChange={handleSelectAllChange} />
          <label htmlFor="select-all"> Select All</label>
        </div>
        <div>{selectedCount} locations selected</div>
      </div>

      <table className="location-table">
        <thead>
          <tr>
            <th className="table-header">Select</th>
            <th className="table-header">Address</th>
            {/* Render commute constraint columns */}
            {constraints.map((constraint, index) => (
              <th className="table-header" key={`commute-${index}`}>
                Commute to {constraint.alias}
              </th>
            ))}
            {/* Explicitly render school headers separately */}
            <th className="table-header" key="elementary">
              Elementary School
            </th>
            <th className="table-header" key="middle">
              Middle School
            </th>
            <th className="table-header" key="high">
              High School
            </th>
          </tr>
        </thead>
        <tbody>
          {combinedData.map((item, rowIndex) => {
            // Find schools of each type
            const elementarySchool = item.schools.find(
              (school) => school.schoolName.toLowerCase().includes("elementry") || school.schoolName.toLowerCase().includes("elementary")
            );

            const middleSchool = item.schools.find((school) => school.schoolName.toLowerCase().includes("middle"));

            const highSchool = item.schools.find((school) => school.schoolName.toLowerCase().includes("high"));

            // Generate all the cells for this row in advance
            const commuteCells = constraints.map((constraint, colIndex) => {
              const times = item.commuteTimesArray[colIndex];
              const status = item.commuteStatusArray[colIndex];
              const statusClass = getStatusClass(status);

              return (
                <td className={`commute-result-cell ${statusClass}`} key={`commute-${rowIndex}-${colIndex}`}>
                  {times ? (
                    <div>
                      <div>
                        <span className="label">No traffic:</span>
                        <span className="value">{times.noTraffic.text}</span>
                      </div>
                      <div>
                        <span className="label">With traffic:</span>
                        <span className={`value ${times.withTraffic.value > times.noTraffic.value * 1.2 ? "highlight" : ""}`}>
                          {times.withTraffic.text}
                          {times.withTraffic.value > times.noTraffic.value * 1.2 && " ⚠️"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <span className="value">Not available</span>
                  )}
                </td>
              );
            });

            const addressStatusClass = getStatusClass(item.commuteStatus);

            return (
              <tr className="table-row" key={`row-${rowIndex}`}>
                <td className="checkbox-cell">
                  <input type="checkbox" className="checkbox" checked={selectedLocations[rowIndex] || false} onChange={() => handleCheckboxChange(rowIndex)} />
                </td>
                <td className={`address-cell ${addressStatusClass}`}>{item.address}</td>

                {/* Insert all commute cells - explicitly separate from other cells */}
                {commuteCells}

                {/* Render school columns separately */}
                <td className="table-cell" key={`elementary-${rowIndex}`}>
                  {elementarySchool ? (
                    <div className="school-rating">
                      {elementarySchool.schoolName}
                      <span className={`rating ${getRatingClass(elementarySchool.rating)}`}>{elementarySchool.rating}</span>
                    </div>
                  ) : (
                    "No data"
                  )}
                </td>

                <td className="table-cell" key={`middle-${rowIndex}`}>
                  {middleSchool ? (
                    <div className="school-rating">
                      {middleSchool.schoolName}
                      <span className={`rating ${getRatingClass(middleSchool.rating)}`}>{middleSchool.rating}</span>
                    </div>
                  ) : (
                    "No data"
                  )}
                </td>

                <td className="table-cell" key={`high-${rowIndex}`}>
                  {/* Improve high school detection */}
                  {highSchool ? (
                    <div className="school-rating">
                      {highSchool.schoolName}
                      <span className={`rating ${getRatingClass(highSchool.rating)}`}>{highSchool.rating}</span>
                    </div>
                  ) : (
                    (() => {
                      // Try a broader search if direct match failed
                      const alternativeHighSchool = item.schools.find(
                        (school) =>
                          school.schoolName.toLowerCase().includes("high") ||
                          school.schoolName.toLowerCase().includes("h.s.") ||
                          school.schoolName.toLowerCase().includes("h s") ||
                          school.schoolType?.toLowerCase() === "high"
                      );

                      return alternativeHighSchool ? (
                        <div className="school-rating">
                          {alternativeHighSchool.schoolName}
                          <span className={`rating ${getRatingClass(alternativeHighSchool.rating)}`}>{alternativeHighSchool.rating}</span>
                        </div>
                      ) : (
                        "No data"
                      );
                    })()
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Add prop types documentation
LocationDetailsTable.defaultProps = {
  onSelectionChange: () => {}, // Default no-op function if no callback provided
};

export default LocationDetailsTable;
